import { ActionReducerMapBuilder, PayloadAction, createSlice } from '@reduxjs/toolkit'
import { withLoadingReducer } from 'utils/reducerHandler'
import { GetFeatureServiceBulkUploadResponseType } from 'features/BulkUpload/services/bulkUpload'
import { fetchGetFeatureServiceBulkUpload } from 'features/BulkUpload/redux'
import {
  GetUserDetailResponseType,
  GetUserListResponseType,
} from 'features/Account/services/intoolsAccess/user'
import {
  SLICE_NAME,
  fetchGetRoles,
  fetchGetUserList,
  fetchPatchActivateAccount,
  fetchPatchDeactivateAccount,
  fetchGetUserDetail,
} from './thunk'

export type UserIntoolsAccessSliceType = {
  isLoading: boolean
  feature: {
    bulkUpload: Nullable<GetFeatureServiceBulkUploadResponseType['data']>
    drawerAddAccount: {
      isOpen: boolean
      data?: GetUserDetailResponseType
    }
  }
  constants: {
    roles: {
      id: number
      name: string
    }[]
  }
  data: GetUserListResponseType['users']
  pagination: {
    pageSize: number
    pageIndex: number
    numberOfElements: number
  }
  form: {
    id?: number
    name: string
    phoneNumber: string
    email: string
    position: string
    locations: {
      location_id: number
    }[]
    roles: {
      id: number
      name: string
    }[]
  }
}

const initialState: UserIntoolsAccessSliceType = {
  isLoading: false,
  feature: {
    bulkUpload: null,
    drawerAddAccount: {
      isOpen: false,
    },
  },
  constants: {
    roles: [],
  },
  data: [],
  pagination: {
    pageSize: 20,
    pageIndex: 0,
    numberOfElements: 0,
  },
  form: {
    name: '',
    phoneNumber: '',
    email: '',
    position: '',
    locations: [],
    roles: [],
  },
}

const userIntoolsAccess = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    resetFormDetailInformation: (state) => {
      state.form.id = undefined
      state.form.name = ''
      state.form.phoneNumber = ''
      state.form.email = ''
      state.form.position = ''
    },
    setOpenDrawerAddAccount: (
      state,
      action: PayloadAction<UserIntoolsAccessSliceType['feature']['drawerAddAccount']>,
    ) => {
      state.feature.drawerAddAccount = action.payload

      const { isOpen } = action.payload
      if (!isOpen) {
        state.form = initialState.form
      }
    },
    setAccountForm: (
      state,
      action: PayloadAction<{
        key: keyof UserIntoolsAccessSliceType['form']
        value: never
      }>,
    ) => {
      const { key, value } = action.payload
      state.form[key] = value
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<UserIntoolsAccessSliceType>) => {
      builder
        .addCase(fetchGetFeatureServiceBulkUpload.fulfilled, (state, action) => {
          state.feature.bulkUpload = action.payload
        })
        .addCase(fetchGetRoles.fulfilled, (state, action) => {
          state.constants.roles = action.payload.data.roles
        })
        .addCase(fetchGetUserList.fulfilled, (state, action) => {
          const { data, pagination } = action.payload

          state.data = data.users

          if (pagination) {
            state.pagination = {
              pageSize: pagination.page_size as number,
              pageIndex: pagination.page_index as number,
              numberOfElements: pagination.number_of_elements as number,
            }
          }
        })
        .addCase(fetchGetUserDetail.fulfilled, (state, action) => {
          const { full_name, email, id, phone_number, position, roles } = action.payload.data

          state.feature.drawerAddAccount.data = action.payload.data

          state.form.id = id
          state.form.name = full_name
          state.form.email = email
          state.form.phoneNumber = phone_number
          state.form.position = position

          state.form.roles = roles.map(
            ({ id: roleId, name: roleName }: { id: number; name: string }) => ({
              id: roleId,
              name: roleName,
            }),
          )
        })
    },
    [fetchGetUserList, fetchGetUserDetail, fetchPatchActivateAccount, fetchPatchDeactivateAccount],
  ),
})

export default userIntoolsAccess.reducer
export const { reset, resetFormDetailInformation, setOpenDrawerAddAccount, setAccountForm } =
  userIntoolsAccess.actions
